import { className } from '@empori/util';
import * as React from 'react';
import { InputItem } from '../../empracoTypes';
import { FaIcon } from '../../generic/icon';

interface FormCheckboxesProps {
  input: InputItem;
}

export default function FormCheckboxes({ input }: FormCheckboxesProps) {
  if (!input) {
    return null;
  }

  const { alternatives } = input;

  if (!alternatives || alternatives && alternatives.length < 1) {
    return null;
  }

  const [values, setValues] = React.useState<string[]>(input?.values ?? []);
  const hiddenInput = React.useRef<HTMLInputElement | null>(null);

  const validationMessage = alternatives.length > 1
    ? 'At least one alternative is required.'
    : 'This check is required.';

  React.useEffect(() => {
    if (hiddenInput.current) {
      if (values.length) {
        hiddenInput.current.setCustomValidity('');
      } else {
        hiddenInput.current.setCustomValidity(validationMessage);
      }
    }
  }, [values]);

  const valuesToSend = () => {
    if (alternatives.length === 1 && values && values.length) {
      return 'Checked';
    }

    if (alternatives.length > 1 && values && values.length) {
      return values.join(', ');
    }

    return '';
  };

  const handleValues = (value: string) => {
    if (values.includes(value)) {
      setValues(values.filter(existingValue => existingValue !== value));
      return;
    }

    setValues([...values, value]);
  };

  return (
    <>
      <p className='form-label-text'>
        {input.label}
        {input.required &&
          <span className='input-required-indicator'>*</span>}
      </p>
      {alternatives.map((item, index) =>
        <Checkbox
          key={`value-${index + 1}`}
          value={item.name}
          click={handleValues}
          checked={values.includes(item.name)} />)}
      {input.description &&
        <p className='input-desc'>
          <span>
            {input.description}
          </span>
        </p>}
      <p className='error-desc' hidden>Please select atleast one of the checkboxes.</p>
      <input
        ref={hiddenInput}
        required={input.required}
        disabled={!input.required && values.length === 0}
        name={input.uniqueName}
        type='text'
        className='empori-hidden-input'
        value={valuesToSend()}
        onChange={() => null} />
    </>
  );
}

interface CheckboxProps {
  value: string;
  checked: boolean;
  click: (value: string) => void;
}

function Checkbox({ value, checked, click }: CheckboxProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      click(value);
    }
  };

  return (
    <label onClick={() => click(value)} style={{ cursor: 'pointer' }} tabIndex={0}>
      <div
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => click(value)}
        onKeyDown={handleKeyDown}
        className={className('checkbox-checkmark', { checked })}
      >
        <FaIcon clsName='fa-regular fa-check' />
      </div>
      <span id={`checkbox-label-${value}`} style={{ marginLeft: '8px' }}>
        {value}
      </span>
    </label>
  );
}